import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { groupCode } from '@/lib/schema/tvControllerGroup';
import { useEffect, useState } from 'react';

type GroupNameDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  initialName: string;
  /** Names already taken by other groups (lower-cased). The QR code is
   *  derived from the name, so names must be unique across the whole tree. */
  takenNames: Set<string>;
  submitLabel: string;
  onSubmit: (name: string) => Promise<void>;
};

export function GroupNameDialog({
  open,
  onOpenChange,
  title,
  description,
  initialName,
  takenNames,
  submitLabel,
  onSubmit,
}: GroupNameDialogProps) {
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const trimmed = name.trim();
  const error =
    trimmed.length > 100
      ? 'Name is too long'
      : takenNames.has(trimmed.toLowerCase())
        ? 'Name must be unique'
        : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!trimmed || error) return;
            setIsSaving(true);
            try {
              await onSubmit(trimmed);
              onOpenChange(false);
            } catch {
              // The page already toasted the failure; keep the dialog open.
            } finally {
              setIsSaving(false);
            }
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="group-name">Name</Label>
            <Input
              id="group-name"
              autoComplete="off"
              autoFocus
              inError={!!error}
              placeholder="Enter group name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <p className="text-sm text-muted-foreground">
            QR code:{' '}
            <span className="font-mono">
              {trimmed ? groupCode(trimmed) : '—'}
            </span>
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed || !!error || isSaving}>
              {isSaving ? 'Saving...' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
